import axios from "axios";
import { apiRequest } from "../api";

const BASE_URL = import.meta.env.VITE_API_URL as string;

const getClientAuthHeaders = () => {
  const token = localStorage.getItem("client_token");
  return token ? { Authorization: token } : {};
};

export interface TicketAttachmentRef {
  url: string;
  name: string;
  mimeType: string;
  size?: number;
  transcript?: string;
}

export interface ClientTicket {
  _id: string;
  protocolId: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  clientId: string;
  organizationId: string;
  createdBy?: string;
  memberIds?: string[];
  memberUsers?: Array<{ _id: string; name: string; email?: string }>;
  attachments?: TicketAttachmentRef[];
  createdAt: string;
  updatedAt: string;
}

export interface ClientTicketMessage {
  _id: string;
  content: { msg?: string; attachments?: TicketAttachmentRef[] };
  type: string;
  createdAt: string;
  agent?: { _id: string; name: string; email?: string };
  contact?: { _id: string; name: string; phone?: string; email?: string };
}

export interface ClientUserOption {
  _id: string;
  name: string;
  email: string;
}

export const clientTicketService = {
  async getClientUsers() {
    return apiRequest<ClientUserOption[]>({
      loaderKey: "clientUsers",
      method: "get",
      url: "/app/client/tickets/users",
    });
  },

  async getTickets(params?: { page?: number; limit?: number; status?: string[]; priority?: string[] }) {
    return apiRequest<{ result: ClientTicket[]; total: number; qtdPages: number }>({
      loaderKey: "clientTickets",
      method: "get",
      url: "/app/client/tickets",
      params,
    });
  },

  async getTicket(id: string) {
    return apiRequest<ClientTicket>({
      loaderKey: "clientTicket",
      method: "get",
      url: `/app/client/tickets/${id}`,
    });
  },

  async createTicket(data: {
    title: string;
    description: string;
    priority?: string;
    attachments?: TicketAttachmentRef[];
  }) {
    return apiRequest<ClientTicket>({
      loaderKey: "createClientTicket",
      method: "post",
      url: "/app/client/tickets",
      data,
    });
  },

  /** Upload antes de criar ticket ou enviar mensagem */
  async uploadAttachment(file: File): Promise<[TicketAttachmentRef | null, { msg?: string } | null]> {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post<{ url: string; name: string; mimeType: string; size: number }>(
        `${BASE_URL}/app/client/tickets/upload`,
        formData,
        { headers: getClientAuthHeaders() }
      );
      const d = res.data;
      return [{ url: d.url, name: d.name, mimeType: d.mimeType, size: d.size }, null];
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { error?: { msg?: string } } };
      };
      return [null, { msg: err?.response?.data?.error?.msg || "Falha no upload" }];
    }
  },

  async updateTicket(id: string, data: { status: "resolved" | "open" }) {
    return apiRequest<ClientTicket>({
      loaderKey: "updateClientTicket",
      method: "put",
      url: `/app/client/tickets/${id}`,
      data,
    });
  },

  async getMessages(ticketId: string, page = 1, limit = 20) {
    return apiRequest<{ result: ClientTicketMessage[]; total: number; qtdPages: number }>({
      loaderKey: "clientTicketMessages",
      method: "get",
      url: `/app/client/tickets/${ticketId}/messages`,
      params: { page, limit },
    });
  },

  async sendMessage(ticketId: string, payload: { content?: string; attachments?: TicketAttachmentRef[] }) {
    return apiRequest<{ success: boolean; result: ClientTicketMessage }>({
      loaderKey: "sendClientMessage",
      method: "post",
      url: `/app/client/tickets/${ticketId}/send-message`,
      data: payload,
    });
  },

  async addMembers(ticketId: string, userIds: string[]) {
    return apiRequest<ClientTicket>({
      loaderKey: "addTicketMembers",
      method: "post",
      url: `/app/client/tickets/${ticketId}/members`,
      data: { userIds },
    });
  },

  async removeMember(ticketId: string, userId: string) {
    return apiRequest<ClientTicket>({
      loaderKey: "removeTicketMember",
      method: "delete",
      url: `/app/client/tickets/${ticketId}/members/${userId}`,
    });
  },

  /** IA: título + descrição a partir de rascunho (gpt-4.1-mini) */
  async suggestFromDraft(draftText: string) {
    return apiRequest<{ title: string; description: string }>({
      loaderKey: "ticketAiSuggest",
      method: "post",
      url: "/app/client/tickets/ai/suggest",
      data: { draftText },
      ignoreError: true,
    });
  },

  /** IA: melhorar texto da mensagem */
  async polishMessage(draftText: string) {
    return apiRequest<{ text: string }>({
      loaderKey: "ticketAiPolish",
      method: "post",
      url: "/app/client/tickets/ai/polish",
      data: { draftText },
      ignoreError: true,
    });
  },

  /** IA: transcrever áudio (Whisper) */
  async transcribeAudio(file: File): Promise<[string | null, { msg?: string } | null]> {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post<{ text: string }>(
        `${BASE_URL}/app/client/tickets/ai/transcribe`,
        formData,
        { headers: getClientAuthHeaders() }
      );
      return [res.data.text, null];
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { error?: { msg?: string } } };
      };
      return [null, { msg: err?.response?.data?.error?.msg || "Falha na transcrição" }];
    }
  },

  /** IA: transcrever pelo URL (download no backend) */
  async transcribeAudioFromUrl(url: string): Promise<[string | null, { msg?: string } | null]> {
    try {
      const res = await axios.post<{ text: string }>(
        `${BASE_URL}/app/client/tickets/ai/transcribe-url`,
        { url },
        { headers: { ...getClientAuthHeaders(), "Content-Type": "application/json" } }
      );
      return [res.data.text, null];
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { error?: { msg?: string } } };
      };
      return [null, { msg: err?.response?.data?.error?.msg || "Falha na transcrição" }];
    }
  },

  /** IA: transcrever arquivo e gerar título + descrição (backend) */
  async draftFromAudio(file: File): Promise<[ { title: string; description: string } | null, { msg?: string } | null ]> {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post<{ title: string; description: string }>(
        `${BASE_URL}/app/client/tickets/ai/draft-from-audio`,
        formData,
        { headers: getClientAuthHeaders() }
      );
      return [res.data, null];
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { error?: { msg?: string } } };
      };
      return [null, { msg: err?.response?.data?.error?.msg || "Falha ao processar o áudio" }];
    }
  },
};
